import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, ClipboardCheck, TrendingUp, Users, User, Stethoscope, FileText, AlertCircle, Play, TestTube } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { InstructionTab } from '@/components/simulation/InstructionTab';
import { EvaluationTab } from '@/components/simulation/EvaluationTab';
import { ProgressTab } from '@/components/simulation/ProgressTab';
import { TeamPerformanceTab } from '@/components/simulation/TeamPerformanceTab';
import { CoachingContextBanner } from '@/components/simulation/CoachingContextBanner';
import { PresentingComplaintTab, MedicalHistoryTab, PhysicalAssessmentTab, DiagnosticsTab } from '@/components/simulation/ClinicalTabs';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatDisplayContent } from '@/lib/utils';

type TabId = 'instruction' | 'presenting' | 'history' | 'physical' | 'diagnostics' | 'evaluation' | 'progress' | 'team';

// Profile row component for patient demographics
const ProfileRow = ({ label, value }: { label: string; value: string | number | null | undefined }) => (
  <div className="flex justify-between items-center py-3 border-b border-border last:border-0">
    <span className="text-muted-foreground">{label}:</span>
    <span className="font-medium text-foreground">{value || '-'}</span>
  </div>
);

export function SimulationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabId>('instruction');
  const [showSimulation, setShowSimulation] = useState(false);

  const { data: agent, isLoading, error } = useQuery({
    queryKey: ['agent', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('simulation_agents')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    }
  });

  // Check if user has completed any sessions for this agent
  const { data: userSessions } = useQuery({
    queryKey: ['user-sessions', id, user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('simulation_evaluations')
        .select('id')
        .eq('agent_id', id)
        .eq('user_id', user.id)
        .is('deleted_at', null)
        .limit(1);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id && !!id
  });

  const hasCompletedSessions = (userSessions?.length || 0) > 0;

  const handleEvaluationSubmitted = () => {
    setActiveTab('evaluation');
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['evaluations', id, user?.id] });
      queryClient.invalidateQueries({ queryKey: ['user-sessions', id, user?.id] });
    }, 10000);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading agent...</p>
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-foreground mb-4">Agent Not Found</h1>
          <p className="text-muted-foreground mb-6">The simulation agent you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/')}
            className="text-primary hover:underline flex items-center gap-2 mx-auto"
          >
            <ArrowLeft size={20} />
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Check if this is a patient type for clinical layout
  const isPatient = agent.type === 'patient';

  // Patient clinical tabs (BEFORE simulation)
  const patientClinicalTabs = [
    { id: 'instruction' as TabId, label: 'Instruction', icon: BookOpen },
    { id: 'presenting' as TabId, label: 'Presenting Complaint', icon: AlertCircle },
    { id: 'history' as TabId, label: 'Medical History', icon: FileText },
    { id: 'physical' as TabId, label: 'Physical Assessment', icon: Stethoscope },
    { id: 'diagnostics' as TabId, label: 'Diagnostics', icon: TestTube },
  ];

  // Patient post-simulation tabs (AFTER simulation)
  const patientPostSimTabs = [
    { id: 'evaluation' as TabId, label: 'Session Evaluations', icon: ClipboardCheck },
    { id: 'progress' as TabId, label: 'Progress', icon: TrendingUp },
    { id: 'team' as TabId, label: 'Team Performance', icon: Users },
  ];

  // Non-patient tabs (original)
  const nonPatientTabs = [
    { id: 'instruction' as TabId, label: 'Instruction', icon: BookOpen },
    { id: 'evaluation' as TabId, label: 'Session Evaluations', icon: ClipboardCheck },
    { id: 'progress' as TabId, label: 'Progress', icon: TrendingUp },
    { id: 'team' as TabId, label: 'Team Performance', icon: Users },
  ];

  // Determine which tabs to show for patients
  const patientTabs = hasCompletedSessions 
    ? [...patientClinicalTabs, ...patientPostSimTabs] 
    : patientClinicalTabs;

  const tabs = isPatient ? patientTabs : nonPatientTabs;

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Back button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Dashboard
        </button>

        {/* Coaching Context Banner */}
        <CoachingContextBanner />

        {/* Patient Layout: Horizontal Two-Column */}
        {isPatient ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Patient Profile Card */}
            <div className="lg:col-span-1">
              <Card className="rounded-2xl shadow-lg p-8">
                {/* Profile Image */}
                <div className="flex justify-center mb-6">
                  <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-white shadow-xl">
                    {agent.image_url ? (
                      <img 
                        src={agent.image_url} 
                        alt={agent.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-muted flex items-center justify-center">
                        <User className="w-20 h-20 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Patient Name & Type Badge */}
                <div className="text-center mb-6">
                  <h1 className="text-2xl font-bold text-foreground mb-2">
                    {agent.name}
                  </h1>
                  <span className="inline-block px-3 py-1 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-full">
                    🩺 Patient
                  </span>
                </div>

                {/* Patient Demographics */}
                <div className="space-y-1">
                  <ProfileRow label="Age" value={agent.age} />
                  <ProfileRow label="Gender" value={agent.gender} />
                  <ProfileRow label="Role" value={agent.occupation} />
                </div>

                {/* Additional Badges */}
                <div className="mt-6 flex flex-wrap gap-2 justify-center">
                  {agent.platform && (
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      agent.platform === 'anam' 
                        ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' 
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                    }`}>
                      {agent.platform === 'anam' ? '🎥 Video' : '🎙️ Audio'}
                    </span>
                  )}
                  <span className="px-3 py-1 bg-primary-subtle rounded-full text-xs font-medium text-primary">
                    🎯 Goal: {agent.goal_score}/100
                  </span>
                  {agent.estimated_duration && (
                    <span className="px-3 py-1 bg-muted rounded-full text-xs text-foreground">
                      ⏱️ {agent.estimated_duration} min
                    </span>
                  )}
                </div>

                {/* Active Status */}
                <div className="mt-6 flex justify-center">
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-success/10 text-success rounded-lg font-medium text-sm">
                    <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                    Active
                  </span>
                </div>
              </Card>
            </div>

            {/* Right Column - Tabbed Content */}
            <div className="lg:col-span-2">
              <Card className="rounded-2xl shadow-lg overflow-hidden">
                {/* Tab Navigation */}
                <div className="border-b border-border">
                  <nav className="flex overflow-x-auto">
                    {tabs.map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`flex items-center gap-2 px-4 md:px-6 py-4 text-xs md:text-sm font-medium whitespace-nowrap transition-colors ${
                            activeTab === tab.id
                              ? 'text-primary border-b-2 border-primary bg-primary/5'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          {tab.label}
                        </button>
                      );
                    })}
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="p-6 md:p-8">
                  {/* Instruction Tab with Start Simulation */}
                  {activeTab === 'instruction' && !showSimulation && (
                    <div className="space-y-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <BookOpen className="w-5 h-5 text-primary" />
                        </div>
                        <h3 className="text-xl font-semibold">Background And Instructions</h3>
                      </div>
                      
                      <Card className="p-6 bg-muted/30">
                        <p className="text-sm text-muted-foreground mb-4">Review these materials before starting the simulation</p>
                        <div 
                          className="prose prose-sm max-w-none dark:prose-invert text-foreground leading-relaxed"
                          dangerouslySetInnerHTML={{ 
                            __html: formatDisplayContent(agent.objectives || 'You are about to conduct a patient consultation. Take a thorough history, demonstrate appropriate clinical communication skills, and build rapport with your patient.')
                          }}
                        />
                        {agent.estimated_duration && (
                          <p className="mt-4 text-sm text-muted-foreground">
                            You will have {agent.estimated_duration} minutes to complete this consultation.
                          </p>
                        )}
                      </Card>

                      {/* Simulation Objective */}
                      <Card className="p-6 border-l-4 border-primary bg-primary/5">
                        <h4 className="font-semibold text-lg mb-2">Simulation Objective</h4>
                        <p className="text-muted-foreground">
                          {agent.title || 'Assess clinical knowledge and communication skills'}
                        </p>
                      </Card>

                      {/* Duration */}
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <span>⏱️</span>
                        <span>Approximately {agent.estimated_duration || '15-20'} minutes</span>
                      </div>

                      {/* Start Simulation Button */}
                      <Button 
                        onClick={() => setShowSimulation(true)}
                        className="w-full h-14 text-lg font-semibold bg-[#1B4D3E] hover:bg-[#163d32] text-white"
                      >
                        <Play className="w-5 h-5 mr-2" />
                        Start Simulation
                      </Button>
                    </div>
                  )}

                  {/* Actual Simulation Interface */}
                  {activeTab === 'instruction' && showSimulation && (
                    <InstructionTab 
                      goalScore={agent.goal_score}
                      agentId={agent.id}
                      agentName={agent.name}
                      agentType={agent.type}
                      userId={user?.id || ''}
                      userName={user?.name || ''}
                      userEmail={user?.email || ''}
                      companyId={user?.company_id}
                      objectives={agent.objectives || 'Assess clinical knowledge and communication skills'}
                      elevenLabsAgentId={agent.elevenlabs_agent_id}
                      platform={agent.platform}
                      evaluationRubric={agent.evaluation_rubric as Array<{criterion: string; description: string; weight: number}> | undefined}
                      onEvaluationSubmitted={handleEvaluationSubmitted}
                      agentImageUrl={agent.image_url}
                      agentInitials={agent.initials || agent.name.substring(0, 2).toUpperCase()}
                      agentTitle={agent.title}
                      prepMaterials={agent.prep_materials}
                      estimatedDuration={agent.estimated_duration}
                      showRubricToUsers={agent.show_rubric_to_users !== false}
                    />
                  )}

                  {/* Clinical Tabs */}
                  {activeTab === 'presenting' && (
                    <PresentingComplaintTab data={agent.presenting_complaint} />
                  )}
                  {activeTab === 'history' && (
                    <MedicalHistoryTab data={agent.medical_history} />
                  )}
                  {activeTab === 'physical' && (
                    <PhysicalAssessmentTab 
                      assessments={agent.physical_assessments as Array<{type: string; findings: string}> | undefined}
                      afterConsultation={agent.physical_assessment_after_consultation || false}
                    />
                  )}
                  {activeTab === 'diagnostics' && (
                    <DiagnosticsTab 
                      tests={agent.diagnostic_tests as Array<{name: string; file?: string; fileUrl?: string; description: string; results?: string}> | undefined}
                      afterConsultation={agent.diagnostic_tests_after_consultation || false}
                    />
                  )}

                  {/* Post-Simulation Tabs */}
                  {activeTab === 'evaluation' && (
                    <EvaluationTab 
                      agentId={id!}
                      userId={user?.id || ''}
                    />
                  )}
                  {activeTab === 'progress' && (
                    <ProgressTab 
                      agentId={id!}
                      userId={user?.id || ''}
                      goalScore={agent.goal_score}
                    />
                  )}
                  {activeTab === 'team' && <TeamPerformanceTab agentId={id!} />}
                </div>
              </Card>
            </div>
          </div>
        ) : (
          /* Non-Patient Layout: Original Vertical Layout */
          <>
            {/* Header Section */}
            <div className="bg-card border border-border rounded-2xl mb-6">
              <div className="p-6">
                {/* Agent Profile Section */}
                <div className="flex items-start gap-6 mb-6 flex-col sm:flex-row">
                  {/* Avatar */}
                  <div className="relative mx-auto sm:mx-0">
                    <div className="w-32 h-32 sm:w-48 sm:h-48 rounded-full bg-gradient-to-br from-primary-subtle to-primary/10 flex items-center justify-center overflow-hidden">
                      {agent.image_url ? (
                        <img src={agent.image_url} alt={agent.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-3xl sm:text-5xl font-bold text-primary">{agent.initials}</span>
                      )}
                    </div>
                    <div className="absolute bottom-2 right-2 bg-success w-6 h-6 rounded-full border-4 border-card"></div>
                  </div>
                  
                  {/* Agent Details */}
                  <div className="flex-1 text-center sm:text-left">
                    <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-2">{agent.name}</h1>
                    <p className="text-lg sm:text-xl text-muted-foreground mb-4">{agent.title}</p>
                    
                    {/* Metadata badges */}
                    <div className="flex flex-wrap gap-3 mb-4 justify-center sm:justify-start">
                      {agent.type && (
                        <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                          agent.type === 'coach' 
                            ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' 
                            : agent.type === 'customer'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                            : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                        }`}>
                          {agent.type === 'coach' ? '🧠 Coach' 
                            : agent.type === 'customer' ? '💼 Customer'
                            : '📋 Examiner'}
                        </span>
                      )}
                      {agent.platform && (
                        <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                          agent.platform === 'anam' 
                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' 
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                        }`}>
                          {agent.platform === 'anam' ? '🎥 Video' : '🎙️ Audio'}
                        </span>
                      )}
                      <span className="px-4 py-2 bg-primary-subtle rounded-full text-sm font-medium text-primary">
                        🎯 Goal: {agent.goal_score}/100
                      </span>
                      {agent.estimated_duration && (
                        <span className="px-4 py-2 bg-muted rounded-full text-sm text-foreground">
                          ⏱️ {agent.estimated_duration} min
                        </span>
                      )}
                    </div>
                    
                    {/* Status badge */}
                    <span className="inline-flex items-center gap-2 px-4 py-2 bg-success/10 text-success rounded-lg font-medium">
                      <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                      Active
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <Card className="rounded-2xl shadow-lg overflow-hidden">
              <div className="border-b border-border">
                <div className="flex gap-4 sm:gap-8 overflow-x-auto px-6">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 py-4 border-b-2 transition-all whitespace-nowrap ${
                          activeTab === tab.id
                            ? 'border-primary text-primary font-semibold'
                            : 'border-transparent text-muted-foreground hover:text-primary'
                        }`}
                      >
                        <Icon size={20} />
                        <span className="hidden sm:inline">{tab.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-6 md:p-8">
                {activeTab === 'instruction' && (
                  <InstructionTab 
                    goalScore={agent.goal_score}
                    agentId={agent.id}
                    agentName={agent.name}
                    agentType={agent.type}
                    userId={user?.id || ''}
                    userName={user?.name || ''}
                    userEmail={user?.email || ''}
                    companyId={user?.company_id}
                    objectives={agent.objectives || 'Practice and improve your skills'}
                    elevenLabsAgentId={agent.elevenlabs_agent_id}
                    platform={agent.platform}
                    evaluationRubric={agent.evaluation_rubric as Array<{criterion: string; description: string; weight: number}> | undefined}
                    onEvaluationSubmitted={handleEvaluationSubmitted}
                    agentImageUrl={agent.image_url}
                    agentInitials={agent.initials || agent.name.substring(0, 2).toUpperCase()}
                    agentTitle={agent.title}
                    prepMaterials={agent.prep_materials}
                    estimatedDuration={agent.estimated_duration}
                    showRubricToUsers={agent.show_rubric_to_users !== false}
                  />
                )}
                {activeTab === 'evaluation' && (
                  <EvaluationTab 
                    agentId={id!}
                    userId={user?.id || ''}
                  />
                )}
                {activeTab === 'progress' && (
                  <ProgressTab 
                    agentId={id!}
                    userId={user?.id || ''}
                    goalScore={agent.goal_score}
                  />
                )}
                {activeTab === 'team' && <TeamPerformanceTab agentId={id!} />}
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
